// iMIS commerce payloads are large, weakly-typed `$type`-tagged objects; we use
// `any` for them deliberately rather than modelling the full contract.
import iMISFetch from "@/utils/iMISFetch";
import { safeAvailable } from "@/store/coupons";
import type { Coupon, OrderResult } from "@/store/types";

/**
 * Places a coupon-discounted BillMe order for a single product.
 *
 * Flow: validate coupon (from the already-loaded object) → build order line →
 * create order → attach the promotion → update pricing → fail-safe (abort if
 * the promo didn't zero the net) → write the CouponUse row → post the
 * ComboOrder (BillMe) → back-fill the OrderNumber onto the CouponUse row.
 *
 * @param id            selectedPartyId — the member the order is for.
 * @param productCode   the product being purchased.
 * @param coupon        the coupon (from CouponsAvailable) redeemed against it.
 * @param promotionCode the iMIS promotion that zeroes the price (e.g. "GDBundle").
 */
export async function createOrderForProduct(
  id: string,
  productCode: string,
  coupon: Coupon | null,
  promotionCode: string,
  note = ""
): Promise<OrderResult> {
  clearReportViewerCookies();

  // Validate against the coupon already loaded from CouponsAvailable (the single
  // source of truth used for the buttons and category chips).
  if (!coupon) return { result: false, message: "No coupon is available for this product" };
  const couponCode = coupon.CouponCode;
  if (coupon.ExpiryDate && new Date(coupon.ExpiryDate) < new Date()) {
    return { result: false, message: `Coupon code ${couponCode} has expired` };
  }
  if (safeAvailable(coupon) <= 0) {
    return { result: false, message: `Coupon code ${couponCode} has no remaining uses` };
  }

  const item = await getItemWithNote(productCode, note);
  if (!item) return { result: false, message: `Product ${productCode} not found` };

  const order = await createOrder(id, id, [item]);
  if (!order) return { result: false, message: "Failed to create order" };

  order.Pricing = {
    $type: "Asi.Soa.Commerce.DataContracts.OrderPricingInformationData, Asi.Contracts",
    Promotions: {
      $type: "Asi.Soa.Commerce.DataContracts.PromotionDataCollection, Asi.Contracts",
      $values: [
        {
          $type: "Asi.Soa.Commerce.DataContracts.PromotionData, Asi.Contracts",
          PromotionCode: promotionCode,
          IsActive: true,
        },
      ],
    },
  };

  const pricedOrder = await getUpdateOrderPricing(order);
  if (!pricedOrder) return { result: false, message: "Failed to update order pricing" };

  // Fail-safe: the coupon must zero the NET price. The promotion leaves
  // TotalBasePrice at the gross amount even on success, so check OrderTotal (the
  // net payable): it's 0 when the promo fully discounts, > 0 when it didn't
  // apply. iMIS omits the Amount field when a monetary value is 0, so an absent
  // Amount means $0. Abort rather than raising an invoice the member never agreed to.
  const netTotal =
    pricedOrder.OrderTotal && pricedOrder.OrderTotal.Amount
      ? parseFloat(pricedOrder.OrderTotal.Amount)
      : 0;
  if (netTotal > 0) {
    return {
      result: false,
      message:
        "Coupon could not be applied (the bundle was not discounted), so no order was placed. Please contact AGD.",
    };
  }

  const couponUse = await createCouponUseRow(id, productCode, couponCode);
  if (!couponUse) return { result: false, message: "Failed to create CouponUse record" };

  await ensureOpenBatch(id);

  const comboOrder = createComboOrder(pricedOrder, true, "REG");
  const orderNumber = await postComboOrder(comboOrder);

  if (!orderNumber) {
    await updateCouponUseRow(id, couponUse.Ordinal, { UpdatedOn: new Date().toISOString() });
    return { result: false, message: "Failed to post order" };
  }

  await updateCouponUseRow(id, couponUse.Ordinal, {
    OrderNumber: orderNumber,
    UpdatedOn: new Date().toISOString(),
  });
  return { result: true, message: `Order number ${orderNumber} created` };
}

/**
 * Clears the ReportViewer cookies that can otherwise bloat request headers past
 * the server limit during a long session. Mirrors the legacy behavior.
 */
function clearReportViewerCookies(): void {
  document.cookie.split(";").forEach((c) => {
    if (c.substring(0, 33) === " /Reserved.ReportViewerWebControl") {
      document.cookie = c + "= ''; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }
  });
}

/** Builds an order line (with note) for the given product code, or undefined if not found. */
async function getItemWithNote(productCode: string, note: string): Promise<any> {
  const res = await iMISFetch<any>("Item?ItemCode=" + productCode, {});
  if (!res.success || res.length === 0) return undefined;
  const list = res.data as any[];
  const d = list[0];
  return {
    IsUnitPriceOverridden: false,
    Note: note || "",
    Item: {
      $type: "Asi.Soa.Commerce.DataContracts.ItemSummaryData, Asi.Contracts",
      ItemClass: d.ItemClass,
      ItemCode: d.ItemCode,
      ItemId: d.ItemId,
      Name: d.Name,
    },
  };
}

/** Finds the member's ship address (used for the order delivery block). */
async function getShipAddress(id: string): Promise<any> {
  const party = await iMISFetch<any>("party", { record: id });
  const addrs = (party.data as any).Addresses.$values as any[];
  for (const addr of addrs) {
    const prefs = addr.CommunicationPreferences.$values as any[];
    for (const pref of prefs) {
      if (pref.Reason === "ship") return addr;
    }
  }
  return null;
}

/** POSTs a new Order for the given items and returns the created order object. */
async function createOrder(id: string, billTo: string, items: any[], quantity = 1): Promise<any> {
  const address = await getShipAddress(id);
  items.forEach((item, i) => {
    item.QuantityOrdered = {
      $type: "System.Nullable`1[[Asi.Soa.Commerce.DataContracts.QuantityData, Asi.Contracts]], mscorlib",
      Amount: quantity,
    };
    item.LineNumber = i + 1;
  });
  const orderPayload = {
    $type: "Asi.Soa.Commerce.DataContracts.OrderData, Asi.Contracts",
    Lines: {
      $type: "Asi.Soa.Commerce.DataContracts.OrderLineDataCollection, Asi.Contracts",
      $values: items,
    },
    Delivery: {
      $type: "Asi.Soa.Commerce.DataContracts.DeliveryDataCollection, Asi.Contracts",
      $values: [
        {
          $type: "Asi.Soa.Commerce.DataContracts.DeliveryData, Asi.Contracts",
          AddressId: address.Address.AddressId,
          Address: address,
          CustomerParty: { $type: "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts", PartyId: id },
          DeliveryMethod: {
            $type: "Asi.Soa.Commerce.DataContracts.DeliveryMethodData, Asi.Contracts",
            DeliveryMethodId: "Fedex",
          },
        },
      ],
    },
    SoldToCustomerParty: { $type: "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts", PartyId: id },
    BillToCustomerParty: { $type: "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts", PartyId: billTo },
    SourceCode: "",
  };
  const posted = await iMISFetch<any>("Order", { method: "POST", data: JSON.stringify(orderPayload) });
  return posted.data;
}

/** Runs the UpdatePricing operation so the promotion is applied to the order. */
async function getUpdateOrderPricing(order: any): Promise<any> {
  const RESTData = {
    $type: "Asi.Soa.Commerce.DataContracts.OrderPriceUpdateRequest, Asi.Contracts",
    EntityTypeName: "Order",
    OperationName: "UpdatePricing",
    Order: order,
  };
  const res = await iMISFetch<any>("Order/_Execute", { method: "POST", data: JSON.stringify(RESTData) });
  return res.data;
}

/**
 * ComboOrder posts against whatever batch is stored in the ordering party's own
 * UserCommercePreferences.CurrentBatchId — there's no field on ComboOrderData itself to specify
 * one (confirmed via decompiling Asi.Contracts.dll). Staff periodically close batches from the
 * back office, which staled this out for a party who has no reason to ever see the staff-only
 * Batch Selector iPart that would normally refresh it — the next ComboOrder POST then 400s with
 * "The selected batch does not have an open status".
 *
 * Writing CurrentBatchId only persists when the write comes from that same party's own identity
 * (confirmed live: an elevated staff token updating its OWN CurrentBatchId persisted; the same
 * token updating a DIFFERENT party's CurrentBatchId returned 200 but silently didn't persist).
 * This function runs under the purchasing member's own session, so it's a same-identity write —
 * best-effort and non-blocking: if it fails, ComboOrder proceeds anyway and fails exactly as it
 * did before this existed.
 */
async function ensureOpenBatch(id: string): Promise<void> {
  const prefsRes = await iMISFetch<any>("UserCommercePreferences", { record: id });
  if (!prefsRes.success) return;
  const prefs = prefsRes.data as any;

  if (prefs.CurrentBatchId) {
    const batchRes = await iMISFetch<any>("BatchSummary", { record: prefs.CurrentBatchId });
    if (batchRes.success && (batchRes.data as any).BatchStatus === 0) return; // already open
  }

  const openRes = await iMISFetch<any>("BatchSummary?BatchStatus=0", { limit: 0 });
  if (!openRes.success) return;
  const openBatches = openRes.data as any[];
  if (openBatches.length === 0) return;
  const latest = openBatches.reduce((a, b) => (new Date(b.BatchDate).getTime() > new Date(a.BatchDate).getTime() ? b : a));
  if (latest.BatchId === prefs.CurrentBatchId) return;

  prefs.CurrentBatchId = latest.BatchId;
  await iMISFetch<any>("UserCommercePreferences", { method: "PUT", record: id, data: JSON.stringify(prefs) });
}

/**
 * Wraps a priced order in a ComboOrder with a BillMe remittance (when raising an invoice).
 *
 * The remittance amount must be the NET total (post-discount), not TotalBasePrice (the gross
 * amount, which iMIS leaves populated even after a 100%-off promotion — see the fail-safe check
 * above this function's caller). Billing the gross amount here silently created a real BillMe
 * invoice for the full price on every coupon-covered "free" purchase, which is what actually
 * required a valid open accounting batch to post — a true $0 order does not. iMIS omits the
 * Amount field entirely when a monetary value is $0, so an absent OrderTotal.Amount means $0.
 */
function createComboOrder(order: any, raiseInvoice: boolean, orderTypeCode?: string): any {
  const netAmount = order.OrderTotal && order.OrderTotal.Amount ? order.OrderTotal.Amount : 0;
  const payments = raiseInvoice
    ? {
        $type: "Asi.Soa.Commerce.DataContracts.RemittanceDataCollection, Asi.Contracts",
        $values: [
          {
            $type: "Asi.Soa.Commerce.DataContracts.RemittanceData, Asi.Contracts",
            Amount: {
              $type: "Asi.Soa.Core.DataContracts.MonetaryAmountData, Asi.Contracts",
              Amount: netAmount,
              Currency: { $type: "Asi.Soa.Core.DataContracts.CurrencyData, Asi.Contracts", DecimalPositions: 2 },
              IsAmountDefined: true,
            },
            PaymentToken: {
              $type: "Asi.Soa.Commerce.DataContracts.AutoPayAccountData, Asi.Contracts",
              PaymentMethodIsReadOnly: false,
            },
            PaymentMethod: {
              $type: "Asi.Soa.Commerce.DataContracts.PaymentMethodData, Asi.Contracts",
              PaymentMethodId: "BillMe",
            },
          },
        ],
      }
    : { $type: "Asi.Soa.Commerce.DataContracts.RemittanceDataCollection, Asi.Contracts", $values: [] };

  const combo: any = {
    $type: "Asi.Soa.Commerce.DataContracts.ComboOrderData, Asi.Contracts",
    Currency: { $type: "Asi.Soa.Core.DataContracts.CurrencyData, Asi.Contracts", CurrencyCode: "USD", DecimalPositions: 2 },
    Invoices: { $type: "Asi.Soa.Commerce.DataContracts.InvoiceSummaryDataCollection, Asi.Contracts", $values: [] },
    Order: order,
    Payments: payments,
  };
  if (orderTypeCode) combo.OrderTypeCode = orderTypeCode;
  return combo;
}

/** POSTs the ComboOrder and returns the resulting OrderNumber. */
async function postComboOrder(comboOrder: any): Promise<string | undefined> {
  const res = await iMISFetch<any>("ComboOrder", { method: "POST", data: JSON.stringify(comboOrder) });
  return (res.data as any)?.Order?.OrderNumber;
}

/** Total AGD_CouponUsev2 write attempts per order: 1 initial + 2 retries. */
const COUPON_USE_WRITE_ATTEMPTS = 3;

/**
 * POSTs a new AGD_CouponUsev2 row (recording the redemption) and returns its
 * Ordinal so the OrderNumber can be back-filled once the order posts.
 *
 * Replaces the original AGD_CouponUse business object, whose Insert operation
 * was confirmed broken server-side in iMIS (every POST returned a 500 with no
 * row committed, regardless of payload — verified via live probing against
 * both devmembers.agd.org and members.agd.org). AGD_CouponUsev2 is a
 * Party-child entity (PrimaryParentEntityTypeName "Party", composite identity
 * `~{partyId}|{Ordinal}`), unlike the old Standalone-parent table. Retries up
 * to COUPON_USE_WRITE_ATTEMPTS total times as a safety net, checking for the
 * false-negative case (POST throws but the row was actually committed) after
 * each failed attempt before giving up.
 */
async function createCouponUseRow(
  id: string,
  productCode: string,
  couponCode: string
): Promise<{ Ordinal: any } | undefined> {
  const now = new Date().toISOString();
  const data = {
    $type: "Asi.Soa.Core.DataContracts.GenericEntityData, Asi.Contracts",
    EntityTypeName: "AGD_CouponUsev2",
    PrimaryParentEntityTypeName: "Party",
    Properties: {
      $type: "Asi.Soa.Core.DataContracts.GenericPropertyDataCollection, Asi.Contracts",
      $values: [
        { $type: "Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts", Name: "ID", Value: id },
        { $type: "Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts", Name: "CreatedOn", Value: now },
        { $type: "Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts", Name: "ProductCode", Value: productCode },
        { $type: "Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts", Name: "CouponCode", Value: couponCode || "" },
      ],
    },
  };

  for (let attempt = 1; attempt <= COUPON_USE_WRITE_ATTEMPTS; attempt++) {
    const res = await iMISFetch<any>("AGD_CouponUsev2", { method: "POST", data: JSON.stringify(data) });
    if (res.success) {
      const values = (res.data as any).Properties.$values as any[];
      const ordinalProp = values.find((p) => p.Name === "Ordinal");
      return { Ordinal: ordinalProp ? ordinalProp.Value.$value : undefined };
    }

    const recovered = await findCouponUseRow(id, productCode, couponCode);
    if (recovered) return recovered;
  }

  return undefined;
}

/**
 * Looks up the not-yet-linked AGD_CouponUsev2 row matching productCode/couponCode
 * for this party, to recover the Ordinal when the creating POST reported
 * failure but actually persisted the row. CreatedOn isn't used to match: iMIS
 * round-trips it lossily (drops the "Z", rounds milliseconds), so it won't
 * equal what was sent. Picks the highest Ordinal among matches (most recent).
 */
async function findCouponUseRow(
  id: string,
  productCode: string,
  couponCode: string
): Promise<{ Ordinal: any } | undefined> {
  const res = await iMISFetch<any>(`AGD_CouponUsev2?id=${id}`, {});
  if (!res.success) return undefined;
  const rows = res.data as any[];
  const get = (row: any, name: string) => (row.Properties.$values as any[]).find((p) => p.Name === name)?.Value;
  const matches = rows.filter(
    (row) => get(row, "ProductCode") === productCode && get(row, "CouponCode") === (couponCode || "") && !get(row, "OrderNumber")
  );
  if (matches.length === 0) return undefined;
  const latest = matches.reduce((a, b) => (Number(get(b, "Ordinal")?.$value) > Number(get(a, "Ordinal")?.$value) ? b : a));
  const ordinalProp = (latest.Properties.$values as any[]).find((p) => p.Name === "Ordinal");
  return { Ordinal: ordinalProp ? ordinalProp.Value.$value : undefined };
}

/**
 * GETs the AGD_CouponUsev2 row, updates the given fields, and PUTs it back.
 * AGD_CouponUsev2 is Party-child with a composite identity, so the record key
 * must be `~{partyId}|{ordinal}` (confirmed live — a bare ordinal or an
 * unprefixed `partyId|ordinal` both 400 "identity did not match expected").
 */
async function updateCouponUseRow(id: string, ordinal: any, fields: Record<string, unknown>): Promise<boolean> {
  const record = `~${id}|${ordinal}`;
  const existing = await iMISFetch<any>("AGD_CouponUsev2", { record });
  if (!existing.success) return false;
  const values = (existing.data as any).Properties.$values as any[];
  for (const key of Object.keys(fields)) {
    const prop = values.find((p) => p.Name === key);
    if (prop) prop.Value = fields[key];
  }
  const res = await iMISFetch<any>("AGD_CouponUsev2", {
    method: "PUT",
    record,
    data: JSON.stringify(existing.data),
  });
  return res.success;
}
